import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { KPICard } from "@/components/KPICard";
import { ChartTooltip } from "@/components/ChartTooltip";
import { fmtEuro, fmtNum, PLATFORM_LABELS, PLATFORM_COLORS } from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { motion } from "framer-motion";
import {
  Wallet, TrendingUp, Route, ListChecks, Timer, Target, Fuel, Receipt, LineChart as LineIcon, Coins,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend, PieChart, Pie, Cell,
} from "recharts";

const CATEGORY_LABELS = {
  carburant: "Carburant", recharge_electrique: "Recharge", lavage: "Lavage",
  telephone: "Téléphone", internet: "Internet", assurance: "Assurance", rc_pro: "RC Pro",
  credit_vehicule: "Crédit véhicule", entretien: "Entretien", pneus: "Pneus", freins: "Freins",
  parking: "Parking", peages: "Péages", autres: "Autres",
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [evo, setEvo] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [expBreak, setExpBreak] = useState([]);
  const [monthly, setMonthly] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, e, p, eb, m] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/stats/evolution?days=30"),
        api.get("/stats/platforms"),
        api.get("/stats/expenses-breakdown"),
        api.get("/stats/monthly?months=6"),
      ]);
      setStats(s.data);
      setEvo(e.data);
      setPlatforms(p.data);
      setExpBreak(eb.data);
      setMonthly(m.data);
    })();
  }, []);

  if (!stats) return <div className="text-muted-foreground">Chargement…</div>;

  const kpis = [
    { label: "CA du jour", value: fmtEuro(stats.ca_today), icon: Wallet, accent: "primary", testid: "kpi-ca-today" },
    { label: "CA semaine", value: fmtEuro(stats.ca_week), icon: TrendingUp, accent: "success", testid: "kpi-ca-week" },
    { label: "CA du mois", value: fmtEuro(stats.ca_month), icon: LineIcon, accent: "primary", testid: "kpi-ca-month", sub: `Objectif ${fmtEuro(stats.monthly_goal)}` },
    { label: "CA annuel", value: fmtEuro(stats.ca_year), icon: TrendingUp, accent: "success", testid: "kpi-ca-year" },
    { label: "Bénéfice du mois", value: fmtEuro(stats.benefit_month), icon: Coins, accent: stats.benefit_month >= 0 ? "success" : "destructive", testid: "kpi-benefit" },
    { label: "Dépenses du mois", value: fmtEuro(stats.expenses_month), icon: Receipt, accent: "destructive", testid: "kpi-expenses" },
    { label: "Kilomètres (mois)", value: `${fmtNum(stats.km_month, 1)} km`, icon: Route, accent: "muted", testid: "kpi-km" },
    { label: "Nombre de courses", value: fmtNum(stats.courses_month), icon: ListChecks, accent: "muted", testid: "kpi-courses" },
    { label: "Revenu disponible", value: fmtEuro(stats.revenu_disponible), icon: Wallet, accent: "success", testid: "kpi-revenu-dispo", sub: `après URSSAF & impôts` },
    { label: "CA / heure", value: fmtEuro(stats.ca_per_hour), icon: Timer, accent: "primary", testid: "kpi-ca-hour" },
    { label: "CA / course", value: fmtEuro(stats.ca_per_course), icon: ListChecks, accent: "primary", testid: "kpi-ca-course" },
    { label: "CA / km", value: fmtEuro(stats.ca_per_km), icon: Fuel, accent: "primary", testid: "kpi-ca-km" },
  ];

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Tableau de bord</div>
          <h1 className="font-manrope text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tighter">
            Votre activité en temps réel
          </h1>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-surface p-4 min-w-[280px]"
          data-testid="progress-goal-card"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-primary" />
              <span className="overline">Objectif mensuel</span>
            </div>
            <span className="text-sm font-semibold num text-primary">{stats.progress_percent}%</span>
          </div>
          <Progress value={Math.min(stats.progress_percent, 100)} className="h-2" />
          <div className="flex justify-between mt-2 text-xs text-muted-foreground num">
            <span>{fmtEuro(stats.ca_month)}</span>
            <span>{fmtEuro(stats.monthly_goal)}</span>
          </div>
        </motion.div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
        {kpis.map((k, i) => (
          <KPICard key={k.label} label={k.label} value={k.value} sublabel={k.sub} icon={k.icon} accent={k.accent} testid={k.testid} delay={i * 0.03} />
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card-surface p-4 lg:col-span-2" data-testid="chart-evolution">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-manrope font-semibold">Évolution — 30 derniers jours</h3>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={evo}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                <Tooltip content={<ChartTooltip valueFormatter={fmtEuro} />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="ca" name="CA" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="depenses" name="Dépenses" stroke="hsl(var(--chart-3))" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="benefice" name="Bénéfice" stroke="hsl(var(--chart-2))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-surface p-4" data-testid="chart-platforms">
          <h3 className="font-manrope font-semibold mb-4">CA par plateforme</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={platforms.map((p) => ({ name: PLATFORM_LABELS[p.platform] || p.platform, value: p.ca, platform: p.platform }))}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={90}
                  innerRadius={45}
                  paddingAngle={2}
                >
                  {platforms.map((p) => (
                    <Cell key={p.platform} fill={PLATFORM_COLORS[p.platform] || "#888"} />
                  ))}
                </Pie>
                <Tooltip content={<ChartTooltip valueFormatter={fmtEuro} />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-surface p-4" data-testid="chart-expenses-breakdown">
          <h3 className="font-manrope font-semibold mb-4">Répartition dépenses (mois)</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={expBreak.map((e) => ({ name: CATEGORY_LABELS[e.category] || e.category, value: e.amount }))} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                <YAxis type="category" dataKey="name" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} width={100} />
                <Tooltip content={<ChartTooltip valueFormatter={fmtEuro} />} />
                <Bar dataKey="value" name="Montant" fill="hsl(var(--chart-3))" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-surface p-4 lg:col-span-2" data-testid="chart-monthly">
          <h3 className="font-manrope font-semibold mb-4">Évolution mensuelle</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                <Tooltip content={<ChartTooltip valueFormatter={fmtEuro} />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="ca" name="CA" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
                <Bar dataKey="depenses" name="Dépenses" fill="hsl(var(--chart-3))" radius={[4, 4, 0, 0]} />
                <Bar dataKey="benefice" name="Bénéfice" fill="hsl(var(--chart-2))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
